import { useContext, useEffect, useState } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { useOutletContext, useParams } from 'react-router-dom';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { useGetSerpDataQuery } from '@/app/Serp/api/getSerpDataApi';
import { contentWebsite } from '@/app/Serp/config/contentBriefColumnConfig';
import { useSelector, useDispatch } from 'react-redux';
import { useGetSerpBriefDataQuery } from '@/app/Serp/api/getSerpBriefApi';
import { serpTooltips } from '@/app/Serp/tooltip/serpTooltips';
import { useGetStatusSerpQuery } from '@/app/Serp/api/historyApi';
import { setSerp } from '@/app/Serp/reducers/serpDataSlice';
import DoughnutCard from '../components/DoughnutCard';
import WordSearchVolume from '../components/WordSearchVolume';
import ProgressCard from '../components/content-brief/ProgressCard';
import CardTabs from '../components/content-brief/CardTabs';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import NoSerpData from '../components/common/NoSerpData';
import DataLoadError from '@/components/errors/DataLoadError';
import {
  shouldShowDataLoadErrorFromQueries,
  refetchQueries,
  isAnyQueryFetching,
  isNetworkApiError
} from '@/app/common/utils/apiErrorUtils';
import { NewSerpContext } from '@/context/Context';
import useDomain from '@/hooks/useDomain';
import CardChartSemiCircleLoading from '../../common/components/skeleton/CardChartSemiCircleLoading';
import CardBarChartLoading from '../../common/components/skeleton/CardBarChartLoading';
import { breakpointsMediaQuery, useMediaQuery } from '@/helpers/utils';
import type { SerpData, SerpDataRootState } from '@/app/Serp/types/serpApiTypes';

type BreadcrumbItem = { text: string; href: string };

type OutletContext = [BreadcrumbItem[], (items: BreadcrumbItem[]) => void];

const ContentBrief = () => {
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  const dispatch = useDispatch();
  const [, setBreadcrumbData] = useOutletContext<OutletContext>();
  const [showCardTabs, setShowCardTabs] = useState(false);
  const { step } = useContext(NewSerpContext);

  useEffect(() => {
    setBreadcrumbData([{ text: 'بریف محتوا', href: '#' }]);
  }, []);

  const serpData = useSelector((state: SerpDataRootState) => state.serpData);
  const { slug } = useParams<{ slug?: string }>();
  const { domain_slug } = useDomain();
  const { data: userInfo } = useGetUserInfoQuery();
  const serp_id = slug === 'last' ? 'last' : slug;
  useGetStatusSerpQuery({
    serp_id: serp_id || 'last',
    domain_slug,
    requested_user: {
      name_family: `${userInfo?.data?.first_name} ${userInfo?.data?.last_name}`,
      email: userInfo?.data?.email ?? ''
    }
  });

  const { data, isLoading, isError, error, refetch, isFetching } =
    useGetSerpDataQuery(
      {
        serp_id: serp_id ?? 'last',
        domain_slug,
        requested_user: {
          name_family: `${userInfo?.data?.first_name} ${userInfo?.data?.last_name}`,
          email: userInfo?.data?.email ?? ''
        }
      },
      {
        skip: step !== 1
      }
    );

  const {
    data: briefData,
    isLoading: isBriefLoading,
    isError: isBriefError,
    error: briefError,
    refetch: refetchBrief,
    isFetching: isBriefFetching
  } = useGetSerpBriefDataQuery(
    {
      serp_id: serp_id ?? 'last',
      domain_slug,
      requested_user: {
        name_family: `${userInfo?.data?.first_name} ${userInfo?.data?.last_name}`,
        email: userInfo?.data?.email ?? ''
      }
    },
    {
      skip: step !== 1
    }
  );

  useEffect(() => {
    if (!data || !briefData) return;
    if (!data.data) {
      dispatch(setSerp(null));
      return;
    }
    const nextSerpData: SerpData = { ...data.data };
    Object.entries(briefData.data ?? {}).forEach(([key, value]) => {
      nextSerpData.analysis_pages = {
        ...nextSerpData.analysis_pages,
        [key]: value
      };
    });
    dispatch(setSerp(nextSerpData));
    setShowCardTabs(true);
  }, [data, briefData]);

  if (
    shouldShowDataLoadErrorFromQueries(
      { isError, error },
      { isError: isBriefError, error: briefError }
    )
  ) {
    return (
      <DataLoadError
        onRetry={() => refetchQueries(refetch, refetchBrief)}
        isRetrying={isAnyQueryFetching(
          { isFetching },
          { isFetching: isBriefFetching }
        )}
        isNetworkError={isNetworkApiError(error) || isNetworkApiError(briefError)}
      />
    );
  }
  if (!isLoading && !isBriefLoading && Object.keys(serpData).length == 0)
    return <NoSerpData />;

  return (
    <>
      <Row className="g-3 mb-3">
        <Col xl={5} md={12} style={{ height: 312 }}>
          {!isLoading &&
          !isBriefLoading &&
          serpData?.keyword_difficulty !== 101 ? (
            <DoughnutCard diff={serpData.keyword_difficulty ?? 0} />
          ) : (
            <CardChartSemiCircleLoading
              title="میزان سختی کلمه"
              tooltip={serpTooltips.wordDiff}
            />
          )}
        </Col>

        <Col xl={7} md={12}>
          {!isLoading && !isBriefLoading ? (
            <WordSearchVolume data={serpData.search_volumes} />
          ) : (
            <CardBarChartLoading
              title="میزان جستجوی کلمه"
              tooltip={serpTooltips.wordSearchVolume}
              limit={30}
              width={10}
              height={15}
              margin={38}
              marginBetweenLine={5}
              showFooter={false}
            />
          )}
        </Col>
      </Row>

      {showCardTabs || step === 5 ? (
        <Row className="g-3 mb-3">
          <Col xs={12} md={6} lg={3}>
            <ProgressCard
              max={serpData?.analysis_pages?.title?.length?.max}
              min={serpData?.analysis_pages?.title?.length?.min}
              range={serpData?.analysis_pages?.title?.length?.avg}
              title={<>میانگین عنوان سئو (بر اساس کاراکتر)</>}
              tooltip={serpTooltips.title}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <ProgressCard
              max={serpData?.analysis_pages?.title?.pixel?.max}
              min={serpData?.analysis_pages?.title?.pixel?.min}
              range={serpData?.analysis_pages?.title?.pixel?.avg}
              title={<>میانگین عنوان سئو (بر اساس پیکسل)</>}
              tooltip={serpTooltips.title}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <ProgressCard
              max={serpData?.analysis_pages?.description?.length?.max}
              min={serpData?.analysis_pages?.description?.length?.min}
              range={serpData?.analysis_pages?.description?.length?.avg}
              title={<>میانگین توضیح متا (بر اساس کاراکتر)</>}
              tooltip={serpTooltips.description}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <ProgressCard
              max={serpData?.analysis_pages?.description?.pixel?.max}
              min={serpData?.analysis_pages?.description?.pixel?.min}
              range={serpData?.analysis_pages?.description?.pixel?.avg}
              title={<>میانگین توضیح متا (بر اساس پیکسل)</>}
              tooltip={serpTooltips.description}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <ProgressCard
              max={serpData?.analysis_pages?.h1?.max}
              min={serpData?.analysis_pages?.h1?.min}
              range={serpData?.analysis_pages?.h1?.avg}
              title={<>میانگین تعداد H1</>}
              tooltip={serpTooltips.h1}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <ProgressCard
              max={serpData?.analysis_pages?.h2?.max}
              min={serpData?.analysis_pages?.h2?.min}
              range={serpData?.analysis_pages?.h2?.avg}
              title={<>میانگین تعداد H2</>}
              tooltip={serpTooltips.h2}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <ProgressCard
              max={serpData?.analysis_pages?.h3?.max}
              min={serpData?.analysis_pages?.h3?.min}
              range={serpData?.analysis_pages?.h3?.avg}
              title={<>میانگین تعداد H3</>}
              tooltip={serpTooltips.h3}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <ProgressCard
              max={serpData?.analysis_pages?.words?.max}
              min={serpData?.analysis_pages?.words?.min}
              range={serpData?.analysis_pages?.words?.avg}
              title={<>میانگین تعداد کلمات صفحات </>}
              tooltip={serpTooltips.pageWords}
            />
          </Col>
        </Row>
      ) : (
        <Row className="g-3 mb-3">
          {['', '', '', '', '', '', '', ''].map((_placeholder, i) => (
            <Col xs={12} md={6} lg={3} key={i}>
              <ProgressCard
                max={0}
                min={0}
                range={0}
                title="درحال بارگذاری"
                tooltip="درحال بارگذاری"
              />
            </Col>
          ))}
        </Row>
      )}

      <Row className="g-3 mb-3">
        {!isLoading && !isBriefLoading ? (
          <Col as={Card} className="p-0">
            <FalconCardHeader
              title={<div>بررسی تعداد عناصر محتوای سایت ها</div>}
              titleTag="h6"
              className="py-2"
              light
            />
            <Card.Body>
              <AgGridWrapper
                settings={{
                  pagination: false,
                  fullscreen: false,
                  changeHeight: false,
                  resetGrid: false,
                  saveGrid: false,
                  sideBar: true,
                  exportBtn: false
                }}
                height="470px"
                // otherOptions={{
                //   getRowStyle: params => {
                //     const totalRows = params.api.getDisplayedRowCount();
                //     const zIndex = totalRows - params.node.rowIndex;
                //     return {
                //       zIndex
                //     };
                //   }
                // }}
                columns={contentWebsite(isMdUp)}
                data={serpData?.items?.filter(x => x.type === 'organic')}
              />
            </Card.Body>
          </Col>
        ) : (
          <AgGridSkeleton height="520px" />
        )}
      </Row>
      {showCardTabs || step === 5 ? (
        <Row className="g-3 mb-3">
          <Col>{serpData && <CardTabs serpData={serpData} />}</Col>
        </Row>
      ) : (
        <Row className="g-3 mb-3">
          <Col>
            <AgGridSkeleton />
          </Col>
        </Row>
      )}
    </>
  );
};

export default ContentBrief;
